import {
  DirectiveDefinitionNode,
  DocumentNode,
  EnumValueDefinitionNode,
  FieldDefinitionNode,
  InputValueDefinitionNode,
  OperationTypeDefinitionNode,
  SchemaDefinitionNode,
  SchemaExtensionNode,
  TypeDefinitionNode,
  TypeExtensionNode,
  TypeSystemDefinitionNode,
  TypeSystemExtensionNode,
} from 'graphql';
import * as monacoEditor from 'monaco-editor';

import {
  cstSharedConversions,
  locationToAstLocation,
  locationToValue,
} from './cstSharedConversions';
import { AllCstTypes } from './generatedSchemaTypes';

export function cstToSchema(
  documentNode: AllCstTypes['TypeSystemExtensionDocument'],
  model: monacoEditor.editor.ITextModel,
  serviceName?: string,
): { documentNode: DocumentNode } {
  const {
    nameDefault,
    namedTypeDefault,
    operationTypeDefault,
    valueDefault,
    typeDefault,
    nameToAst,
    typeToAst,
    namedTypeToAst,
    defaultValueToAst,
    directiveToAst,
    stringToAst,
    operationTypeToAst,
  } = cstSharedConversions(model);

  const documentToAst = (
    node: AllCstTypes['TypeSystemExtensionDocument'],
  ): DocumentNode => {
    return {
      kind: 'Document',
      loc: locationToAstLocation(node.location, model),
      definitions: (
        node.children.TypeSystemDefinitionOrExtension ?? []
      ).flatMap(typeSystemDefinitionOrExtensionToAst),
    };
  };

  const typeSystemDefinitionOrExtensionToAst = (
    node: AllCstTypes['TypeSystemDefinitionOrExtension'],
  ): TypeSystemDefinitionNode | TypeSystemExtensionNode | [] => {
    if (node.children.SchemaDefinition) {
      const schemaDefinition = node.children.SchemaDefinition[0];
      return schemaDefinition ? schemaDefinitionToAst(schemaDefinition) : [];
    }
    if (node.children.SchemaExtension) {
      const schemaExtension = node.children.SchemaExtension[0];
      return schemaExtension ? schemaExtensionToAst(schemaExtension) : [];
    }
    if (node.children.TypeDefinition) {
      const typeDefinition = node.children.TypeDefinition[0];
      return typeDefinition ? typeDefinitionToAst(typeDefinition) : [];
    }
    if (node.children.TypeExtension) {
      const typeExtension = node.children.TypeExtension[0];
      return typeExtension ? typeExtensionToAst(typeExtension) : [];
    }
    if (node.children.DirectiveDefinition) {
      const directiveDefinition = node.children.DirectiveDefinition[0];
      return directiveDefinition
        ? directiveDefinitionToAst(directiveDefinition)
        : [];
    }
    return [];
  };

  const schemaDefinitionToAst = (
    node: AllCstTypes['SchemaDefinition'],
  ): SchemaDefinitionNode => {
    const description = stringToAst(
      node.children.Description?.[0].children.StringValue?.[0],
    );
    const directives = node.children.Directives?.[0].children.Directive;
    const operationTypes = node.children.RootOperationTypeDefinition;
    return {
      kind: 'SchemaDefinition',
      loc: locationToAstLocation(node.location, model),
      directives: directives?.map(directiveToAst),
      operationTypes:
        operationTypes?.map(rootOperationTypeDefinitionToAst) ?? [],
      description,
    };
  };

  const schemaExtensionToAst = (
    node: AllCstTypes['SchemaExtension'],
  ): SchemaExtensionNode => {
    const directives = node.children.Directives?.[0].children.Directive;
    const operationTypes =
      node.children.RootOperationTypeDefinitionList?.[0].children
        .RootOperationTypeDefinition ?? [];
    return {
      kind: 'SchemaExtension',
      loc: locationToAstLocation(node.location, model),
      directives: directives?.map(directiveToAst),
      operationTypes:
        operationTypes?.map(rootOperationTypeDefinitionToAst) ?? [],
    };
  };

  const rootOperationTypeDefinitionToAst = (
    node: AllCstTypes['RootOperationTypeDefinition'],
  ): OperationTypeDefinitionNode => {
    const type = node.children.NamedType?.[0];
    return {
      kind: 'OperationTypeDefinition',
      loc: locationToAstLocation(node.location, model),
      operation:
        operationTypeToAst(node.children.OperationType?.[0]) ??
        operationTypeDefault,
      type: type ? namedTypeToAst(type) : namedTypeDefault(),
    };
  };

  const directiveDefinitionToAst = (
    node: AllCstTypes['DirectiveDefinition'],
  ): DirectiveDefinitionNode => {
    const description = stringToAst(
      node.children.Description?.[0].children.StringValue?.[0],
    );
    const locations =
      node.children.DirectiveLocations?.[0].children.DirectiveLocation;
    const directiveArguments =
      node.children.ArgumentsDefinition?.[0].children.InputValueDefinition;
    return {
      kind: 'DirectiveDefinition',
      loc: locationToAstLocation(node.location, model),
      name: nameToAst(node.children.Name?.[0]) ?? nameDefault(),
      locations:
        locations?.flatMap((location) => ({
          kind: 'Name',
          value: locationToValue(location.location, model),
          loc: locationToAstLocation(location.location, model),
        })) ?? [],
      repeatable: false,
      arguments: directiveArguments?.map(inputValueDefinitionToAst),
      description,
    };
  };

  const typeExtensionToAst = (
    node: AllCstTypes['TypeExtension'],
  ): TypeExtensionNode | [] => {
    const baseTypeExtension = {
      loc: locationToAstLocation(node.location, model),
      serviceName,
    };

    if (node.children.ScalarTypeExtension) {
      const typeExtension = node.children.ScalarTypeExtension[0];
      const directives =
        typeExtension.children.Directives?.[0].children.Directive;
      return {
        kind: 'ScalarTypeExtension',
        name: nameToAst(typeExtension.children.Name?.[0]) ?? nameDefault(),
        directives: directives?.map(directiveToAst),
        ...baseTypeExtension,
      };
    }
    if (node.children.ObjectTypeExtension) {
      const typeExtension = node.children.ObjectTypeExtension[0];
      const fields =
        typeExtension.children.FieldsDefinition?.[0].children.FieldDefinition;
      const directives =
        typeExtension.children.Directives?.[0].children.Directive;
      const interfaces =
        typeExtension.children.ImplementsInterfaces?.[0].children.NamedType;
      return {
        kind: 'ObjectTypeExtension',
        name: nameToAst(typeExtension.children.Name?.[0]) ?? nameDefault(),
        fields: fields?.map(fieldDefinitionToAst),
        directives: directives?.map(directiveToAst),
        interfaces: interfaces?.map(namedTypeToAst),
        ...baseTypeExtension,
      };
    }
    if (node.children.InterfaceTypeExtension) {
      const typeExtension = node.children.InterfaceTypeExtension[0];
      const fields =
        typeExtension.children.FieldsDefinition?.[0].children.FieldDefinition;
      const directives =
        typeExtension.children.Directives?.[0].children.Directive;
      const interfaces =
        typeExtension.children.ImplementsInterfaces?.[0].children.NamedType;
      return {
        kind: 'InterfaceTypeExtension',
        name: nameToAst(typeExtension.children.Name?.[0]) ?? nameDefault(),
        directives: directives?.map(directiveToAst),
        fields: fields?.map(fieldDefinitionToAst),
        interfaces: interfaces?.map(namedTypeToAst),
        ...baseTypeExtension,
      };
    }
    if (node.children.UnionTypeExtension) {
      const typeExtension = node.children.UnionTypeExtension[0];
      const directives =
        typeExtension.children.Directives?.[0].children.Directive;
      const types =
        typeExtension.children.UnionMemberTypes?.[0].children.NamedType;
      return {
        kind: 'UnionTypeExtension',
        name: nameToAst(typeExtension.children.Name?.[0]) ?? nameDefault(),
        directives: directives?.map(directiveToAst),
        types: types?.map(namedTypeToAst),
        ...baseTypeExtension,
      };
    }
    if (node.children.EnumTypeExtension) {
      const typeExtension = node.children.EnumTypeExtension[0];
      const directives =
        typeExtension.children.Directives?.[0].children.Directive ?? [];
      const values =
        typeExtension.children.EnumValuesDefinition?.[0].children
          .EnumValueDefinition ?? [];
      return {
        kind: 'EnumTypeExtension',
        name: nameToAst(typeExtension.children.Name?.[0]) ?? nameDefault(),
        directives: directives.map(directiveToAst),
        values: values.map(enumValueDefinitionToAst),
        ...baseTypeExtension,
      };
    }
    if (node.children.InputObjectTypeExtension) {
      const typeExtension = node.children.InputObjectTypeExtension[0];
      const directives =
        typeExtension.children.Directives?.[0].children.Directive;
      const fields =
        typeExtension.children.InputFieldsDefinition?.[0].children
          .InputValueDefinition;
      return {
        kind: 'InputObjectTypeExtension',
        name: nameToAst(typeExtension.children.Name?.[0]) ?? nameDefault(),
        directives: directives?.map(directiveToAst),
        fields: fields?.map(inputValueDefinitionToAst),
        ...baseTypeExtension,
      };
    }
    return [];
  };

  const typeDefinitionToAst = (
    node: AllCstTypes['TypeDefinition'],
  ): TypeDefinitionNode | [] => {
    const baseTypeDefinition = {
      description: stringToAst(
        node.children.Description?.[0].children.StringValue?.[0],
      ),
      loc: locationToAstLocation(node.location, model),
      serviceName,
    };

    if (node.children.ScalarTypeDefinition) {
      const typeDefinition = node.children.ScalarTypeDefinition[0];
      const directives =
        typeDefinition.children.Directives?.[0].children.Directive;
      return {
        kind: 'ScalarTypeDefinition',
        name: nameToAst(typeDefinition.children.Name?.[0]) ?? nameDefault(),
        directives: directives?.map(directiveToAst),
        ...baseTypeDefinition,
      };
    }
    if (node.children.ObjectTypeDefinition) {
      const typeDefinition = node.children.ObjectTypeDefinition[0];
      const fields =
        typeDefinition.children.FieldsDefinition?.[0].children.FieldDefinition;
      const directives =
        typeDefinition.children.Directives?.[0].children.Directive;
      const interfaces =
        typeDefinition.children.ImplementsInterfaces?.[0].children.NamedType;
      return {
        kind: 'ObjectTypeDefinition',
        name: nameToAst(typeDefinition.children.Name?.[0]) ?? nameDefault(),
        fields: fields?.map(fieldDefinitionToAst),
        directives: directives?.map(directiveToAst),
        interfaces: interfaces?.map(namedTypeToAst),
        ...baseTypeDefinition,
      };
    }
    if (node.children.InterfaceTypeDefinition) {
      const typeDefinition = node.children.InterfaceTypeDefinition[0];
      const fields =
        typeDefinition.children.FieldsDefinition?.[0].children.FieldDefinition;
      const directives =
        typeDefinition.children.Directives?.[0].children.Directive;
      const interfaces =
        typeDefinition.children.ImplementsInterfaces?.[0].children.NamedType;
      return {
        kind: 'InterfaceTypeDefinition',
        name: nameToAst(typeDefinition.children.Name?.[0]) ?? nameDefault(),
        directives: directives?.map(directiveToAst),
        fields: fields?.map(fieldDefinitionToAst),
        interfaces: interfaces?.map(namedTypeToAst),
        ...baseTypeDefinition,
      };
    }
    if (node.children.UnionTypeDefinition) {
      const typeDefinition = node.children.UnionTypeDefinition[0];
      const directives =
        typeDefinition.children.Directives?.[0].children.Directive;
      const types =
        typeDefinition.children.UnionMemberTypes?.[0].children.NamedType;
      return {
        kind: 'UnionTypeDefinition',
        name: nameToAst(typeDefinition.children.Name?.[0]) ?? nameDefault(),
        directives: directives?.map(directiveToAst),
        types: types?.map(namedTypeToAst),
        ...baseTypeDefinition,
      };
    }
    if (node.children.EnumTypeDefinition) {
      const typeDefinition = node.children.EnumTypeDefinition[0];
      const directives =
        typeDefinition.children.Directives?.[0].children.Directive;
      const values =
        typeDefinition.children.EnumValuesDefinition?.[0].children
          .EnumValueDefinition ?? [];
      return {
        kind: 'EnumTypeDefinition',
        name: nameToAst(typeDefinition.children.Name?.[0]) ?? nameDefault(),
        directives: directives?.map(directiveToAst),
        values: values.map(enumValueDefinitionToAst),
        ...baseTypeDefinition,
      };
    }
    if (node.children.InputObjectTypeDefinition) {
      const typeDefinition = node.children.InputObjectTypeDefinition[0];
      const directives =
        typeDefinition.children.Directives?.[0].children.Directive;
      const fields =
        typeDefinition.children.InputFieldsDefinition?.[0].children
          .InputValueDefinition;
      return {
        kind: 'InputObjectTypeDefinition',
        name: nameToAst(typeDefinition.children.Name?.[0]) ?? nameDefault(),
        directives: directives?.map(directiveToAst),
        fields: fields?.map(inputValueDefinitionToAst),
        ...baseTypeDefinition,
      };
    }
    return [];
  };

  const inputValueDefinitionToAst = (
    node: AllCstTypes['InputValueDefinition'],
  ): InputValueDefinitionNode => {
    const description = stringToAst(
      node.children.Description?.[0].children.StringValue?.[0],
    );
    const directives = node.children.Directives?.[0].children.Directive;
    return {
      kind: 'InputValueDefinition',
      loc: locationToAstLocation(node.location, model),
      name: nameToAst(node.children.Name?.[0]) ?? nameDefault(),
      type: typeToAst(node.children.Type?.[0]) ?? typeDefault(),
      defaultValue:
        defaultValueToAst(node.children.DefaultValue?.[0]) ?? valueDefault(),
      description,
      directives: directives?.map(directiveToAst),
    };
  };

  const enumValueDefinitionToAst = (
    node: AllCstTypes['EnumValueDefinition'],
  ): EnumValueDefinitionNode => {
    const description = stringToAst(
      node.children.Description?.[0].children.StringValue?.[0],
    );
    const directives = node.children.Directives?.[0].children.Directive;
    const name =
      node.children.EnumValue?.[0].children.NameButNotTrueOrFalseOrNull?.[0];
    return {
      kind: 'EnumValueDefinition',
      loc: locationToAstLocation(node.location, model),
      name: name
        ? {
            kind: 'Name',
            value: locationToValue(name.location, model),
            loc: locationToAstLocation(name.location, model),
          }
        : nameDefault(),
      description,
      directives: directives?.map(directiveToAst),
    };
  };

  const fieldDefinitionToAst = (
    node: AllCstTypes['FieldDefinition'],
  ): FieldDefinitionNode => {
    const description = stringToAst(
      node.children.Description?.[0].children.StringValue?.[0],
    );
    const directives = node.children.Directives?.[0].children.Directive;
    const argumentDefinitions =
      node.children.ArgumentsDefinition?.[0].children.InputValueDefinition;
    return {
      kind: 'FieldDefinition',
      loc: locationToAstLocation(node.location, model),
      name: nameToAst(node.children.Name?.[0]) ?? nameDefault(),
      type: typeToAst(node.children.Type?.[0]) ?? typeDefault(),
      arguments: argumentDefinitions?.map(argumentDefinitionToAst),
      description,
      directives: directives?.map(directiveToAst),
    };
  };

  const argumentDefinitionToAst = (
    node: AllCstTypes['InputValueDefinition'],
  ): InputValueDefinitionNode => {
    const description = stringToAst(
      node.children.Description?.[0].children.StringValue?.[0],
    );
    const directives = node.children.Directives?.[0].children.Directive;
    return {
      kind: 'InputValueDefinition',
      loc: locationToAstLocation(node.location, model),
      name: nameToAst(node.children.Name?.[0]) ?? nameDefault(),
      type: typeToAst(node.children.Type?.[0]) ?? typeDefault(),
      description,
      defaultValue:
        defaultValueToAst(node.children.DefaultValue?.[0]) ?? valueDefault(),
      directives: directives?.map(directiveToAst),
    };
  };

  return {
    documentNode: documentToAst(documentNode),
  };
}
